import { Icon12ArrowUp, Icon20Cancel } from '@vkontakte/icons';
import { Button, Spacing, Textarea } from '@vkontakte/vkui';
import classNames from 'clsx';
import debounce from 'lodash.debounce';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiPhotoGenerationStyles } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { generateImageStart } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import { useAiPhotoGenerationPromptKeyDown } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.hooks';
import { getAiGenerationState } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';
import type { AiPhotoGenerationImageStyleType } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.types';
import { renderAiGenerationStylesPopup } from 'reactApp/ui/AiPhotoGeneration/Modals/Styles/AiPhotoGenerationStylesPopup.helpers';
import { AiPhotoGenerationSuggestions } from 'reactApp/ui/AiPhotoGeneration/ToolbarIdle/Suggestions/AiPhotoGenerationSuggestions';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AiPhotoGenerationToolbarIdle.css';

const PLACEHOLDER_TEXT = 'Напишите запрос, например: кот смотрит в окно, за окном цветёт вишня';

const getStyleName = (key: string) => aiPhotoGenerationStyles.find((i) => i.key === key)?.name || key;

// eslint-disable-next-line max-lines-per-function
export const AiPhotoGenerationToolbarIdle = memo(() => {
    const { prompt, style } = useSelector(getAiGenerationState);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(!prompt);
    const [showApplyButton, setShowApplyButton] = useState<boolean>(!!prompt);
    const [selectedStyle, setSelectedStyle] = useState<AiPhotoGenerationImageStyleType | null>(style || null);

    const [value, setValue] = useState(prompt || '');

    const dispatch = useDispatch();

    const onPromptChange = useMemo(
        () =>
            debounce((value: string) => {
                if (value.length !== 0) {
                    setShowSuggestions(false);
                    setShowApplyButton(true);
                } else {
                    setShowSuggestions(true);
                    setShowApplyButton(false);
                }
            }, 200),
        []
    );

    useEffect(() => {
        return () => {
            onPromptChange.cancel();
        };
    }, [onPromptChange]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const nextValue = e.target.value;
            setValue(nextValue);
            onPromptChange(nextValue);
        },
        [onPromptChange]
    );

    const onSuggestionClick = useCallback(
        (suggestion: string) => {
            setValue(suggestion);
            onPromptChange(suggestion);

            sendXray(['ai-gen', 'suggestion', 'click']);
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'suggestion-click',
                generation_suggestion: suggestion,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        },
        [onPromptChange]
    );

    const handleOpenStylesPopup = useCallback(() => {
        sendXray(['ai-gen', 'style-open', 'click']);
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'open-styles-popup',
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user']],
        });

        renderAiGenerationStylesPopup({ setSelectedStyle });
    }, []);

    const handleResetStyle = useCallback(
        (e: React.MouseEvent) => {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'reset-style',
                generation_style: selectedStyle,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
            e.stopPropagation();
            setSelectedStyle(null);
        },
        [selectedStyle]
    );

    const generationPayload = useMemo(() => {
        if (selectedStyle) {
            return {
                prompt: value,
                style: selectedStyle,
            };
        }

        return {
            prompt: value,
        };
    }, [selectedStyle, value]);

    const handleStartGeneration = useCallback(() => {
        if (!generationPayload.prompt.trim()) {
            return;
        }

        dispatch(generateImageStart(generationPayload));
    }, [dispatch, generationPayload]);

    const handleTextareaKeyDown = useAiPhotoGenerationPromptKeyDown({
        handleStartGeneration,
        setValue,
        onPromptChange,
    });

    const handleGenerationClick = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'button-gen-click',
            generation_style: selectedStyle,
            generation_prompt: value?.slice(0, 256),
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user']],
        });

        handleStartGeneration();
    }, [selectedStyle, value, handleStartGeneration]);

    const renderSelectedStyle = useMemo(() => {
        if (selectedStyle) {
            return (
                <div className={styles.selectedButtonWrapper}>
                    {getStyleName(selectedStyle)}
                    <Icon20Cancel className={styles.selectedButtonClose} onClick={handleResetStyle} />
                </div>
            );
        }

        return 'Выбрать стиль';
    }, [handleResetStyle, selectedStyle]);

    return (
        <div className={styles.toolbar} data-test-id="ai-photo-generation-toolbar-idle">
            {showSuggestions && <AiPhotoGenerationSuggestions onSuggestionClick={onSuggestionClick} />}

            <div className={styles.wrapper}>
                <Textarea
                    placeholder={PLACEHOLDER_TEXT}
                    value={value}
                    onChange={handleChange}
                    maxHeight={120}
                    data-test-id="prompt-textarea"
                    className={styles.inputContainer}
                    aria-rowcount={6}
                    rows={1}
                    onKeyDown={handleTextareaKeyDown}
                    maxLength={2048}
                />
                <Spacing size={20} />
                <div className={styles.controls}>
                    <Button
                        size="l"
                        data-test-id="button-select-style"
                        onClick={handleOpenStylesPopup}
                        className={classNames(styles.styleButton, {
                            [styles.styleButtonSelected]: !!selectedStyle,
                        })}
                    >
                        {renderSelectedStyle}
                    </Button>

                    {showApplyButton && (
                        <Button
                            size="l"
                            mode="primary"
                            appearance="accent-invariable"
                            before={<Icon12ArrowUp />}
                            data-test-id="button-start-generation"
                            onClick={handleGenerationClick}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

AiPhotoGenerationToolbarIdle.displayName = 'AiPhotoGenerationToolbarIdle';
